import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Grid,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  alpha,
  keyframes,
  CircularProgress,
  Paper,
  Button,
} from "@mui/material";
import {
  Psychology,
  TrendingUp,
  TrendingDown,
  Warning,
  Lightbulb,
  MonetizationOn,
  CloudQueue,
  Speed,
  Security,
  Savings,
  Analytics,
  AutoAwesome,
  InfoOutlined,
} from "@mui/icons-material";
import { WidgetContainer } from "../widgetContainer/widgetContainer";
import Ec2DetailsModal from "../../modals/ec2DetailsModal";
import { callAPI } from "../../../functions/api";
import { DownloadExcel } from "../../../functions/downloadExcel";

const renderTextWithBold = (text) => {
  if (!text) return null;
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
};

const AIFinanceInsights = ({ recommendations = [], aiAnalysisData, aiAnalysisLoading, onFetchAIAnalysis }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [activeTab, setActiveTab] = useState(0);
  const [selectedEc2, setSelectedEc2] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [predictionsFetched, setPredictionsFetched] = useState(false);

  // Fetch AI analysis when Predictions tab is opened
  useEffect(() => {
    if (activeTab === 2 && !predictionsFetched && onFetchAIAnalysis) {
      onFetchAIAnalysis();
      setPredictionsFetched(true);
    }
  }, [activeTab, predictionsFetched, onFetchAIAnalysis]);

  // Animations
  const pulse = keyframes`
    0% { opacity: 0.8; transform: scale(1); }
    50% { opacity: 1; transform: scale(1.05); }
    100% { opacity: 0.8; transform: scale(1); }
  `;

  const shimmer = keyframes`
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  `;

  const fadeInUp = keyframes`
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  `;

  // Transform API recommendations into insights format
  const getInsightsData = () => {
    const costOptimization = recommendations.map((rec, index) => {
      const bestOption = rec.recommendations?.[0]; // Take the first (best) recommendation
      const savingsValue = bestOption?.savingOpportunity?.estimatedMonthlySavingsValue || 0;
      const savingsPercent = bestOption?.savingOpportunity?.savingsOpportunityPercentage || 0;

      // Extract actual utilization metrics from the data
      const utilizationMetrics = rec.utilizationMetrics || [];
      const getMetricValue = (name) => {
        const metric = utilizationMetrics.find((m) => m.name === name);
        return metric?.value || 0;
      };

      const cpuValue = getMetricValue("Cpu");
      const memoryValue = getMetricValue("Memory");
      const networkInValue = getMetricValue("NETWORK_IN_BYTES_PER_SECOND");
      const networkOutValue = getMetricValue("NETWORK_OUT_BYTES_PER_SECOND");
      const ebsReadValue = getMetricValue("EBS_READ_BYTES_PER_SECOND");
      const ebsWriteValue = getMetricValue("EBS_WRITE_BYTES_PER_SECOND");
      const ebsReadOps = getMetricValue("EBS_READ_OPS_PER_SECOND");
      const ebsWriteOps = getMetricValue("EBS_WRITE_OPS_PER_SECOND");

      // Parse finding reasons into categories with actual values
      const findingCodes = rec.findingReasonCodes?.map((code) => code.code) || [];

      // Generate description based on finding codes
      let descriptionParts = [];
      if (findingCodes.some((c) => c.includes("CPUOverprovisioned"))) {
        descriptionParts.push("CPU");
      }
      if (findingCodes.some((c) => c.includes("MemoryOverprovisioned"))) {
        descriptionParts.push("Memory");
      }
      if (
        findingCodes.some(
          (c) =>
            c.includes("NetworkBandwidthOverprovisioned") || c.includes("NetworkPPSOverprovisioned")
        )
      ) {
        descriptionParts.push("Network");
      }
      if (
        findingCodes.some(
          (c) => c.includes("EBSIOPSOverprovisioned") || c.includes("EBSThroughputOverprovisioned")
        )
      ) {
        descriptionParts.push("EBS");
      }
      if (
        findingCodes.some(
          (c) =>
            c.includes("DiskIOPSOverprovisioned") || c.includes("DiskThroughputOverprovisioned")
        )
      ) {
        descriptionParts.push("Disk I/O");
      }

      const instanceName = rec.instanceId || rec.instanceArn?.split("/").pop() || "Instance";
      const overprovisionedDesc =
        descriptionParts.length > 0
          ? `${instanceName} is overprovisioned on ${descriptionParts.join(", ")}`
          : `${instanceName} can be optimized`;

      // For network: typical high-performance instances can handle 10+ Gbps (1250+ MB/s), so scale accordingly
      const maxNetworkMBps = 1250; // 10 Gbps baseline
      const networkMBps = Math.max(networkInValue, networkOutValue) / 1000000;
      const networkPercent = Math.min((networkMBps / maxNetworkMBps) * 100, 100);

      // For EBS: typical provisioned IOPS can be 3000-64000, use 5000 as baseline
      const maxEbsIOPS = 5000;
      const ebsIOPS = Math.max(ebsReadOps, ebsWriteOps);
      const ebsPercent = Math.min((ebsIOPS / maxEbsIOPS) * 100, 100);

      // Disk throughput in MB/s
      const diskMBps = Math.max(ebsReadValue, ebsWriteValue) / 1000000;
      const maxDiskMBps = 250; // 250 MB/s baseline for disk throughput
      const diskPercent = Math.min((diskMBps / maxDiskMBps) * 100, 100);

      // Build resource metrics from available utilization data
      const resourceMetrics = {};

      // Add all available metrics dynamically
      utilizationMetrics.forEach((metric) => {
        const metricName = metric.name?.toLowerCase() || "";
        const value = metric.value || 0;

        if (metricName === "cpu") {
          resourceMetrics.cpu = {
            value,
            label: `${value.toFixed(1)}%`,
            barWidth: value,
            color: "#ff9800",
            name: "CPU",
          };
        } else if (metricName === "memory") {
          resourceMetrics.memory = {
            value,
            label: `${value.toFixed(1)}%`,
            barWidth: value,
            color: "#4caf50",
            name: "Memory",
          };
        } else if (metricName.includes("network_in") || metricName.includes("network_out")) {
          if (!resourceMetrics.network) {
            resourceMetrics.network = {
              value: networkMBps,
              label: `${networkMBps.toFixed(1)} MB/s`,
              barWidth: networkPercent,
              color: "#2196f3",
              name: "Network",
            };
          }
        } else if (metricName.includes("ebs") && metricName.includes("ops")) {
          if (!resourceMetrics.ebsOps) {
            resourceMetrics.ebsOps = {
              value: ebsIOPS,
              label: `${ebsIOPS.toFixed(0)} IOPS`,
              barWidth: ebsPercent,
              color: "#9c27b0",
              name: "EBS IOPS",
            };
          }
        } else if (metricName.includes("ebs") && metricName.includes("bytes")) {
          if (!resourceMetrics.ebsThroughput) {
            resourceMetrics.ebsThroughput = {
              value: diskMBps,
              label: `${diskMBps.toFixed(1)} MB/s`,
              barWidth: diskPercent,
              color: "#e91e63",
              name: "Disk I/O",
            };
          }
        } else if (
          metricName === "disk_read_ops_per_second" ||
          metricName === "disk_write_ops_per_second"
        ) {
          const diskOps = value;
          const maxDiskOps = 3000;
          const diskOpsPercent = Math.min((diskOps / maxDiskOps) * 100, 100);
          if (!resourceMetrics.diskOps) {
            resourceMetrics.diskOps = {
              value: diskOps,
              label: `${diskOps.toFixed(0)} Ops`,
              barWidth: diskOpsPercent,
              color: "#795548",
              name: "Disk Ops",
            };
          }
        }
      });

      // Calculate priority score: high dollar savings + low risk = high priority (red/urgent)
      const performanceRisk = bestOption?.performanceRisk || 3; // Default to high risk if unknown

      // Score calculation based on DOLLAR AMOUNT not percentage:
      // - High savings (>$500/mo): +3 points
      // - Medium savings ($200-$500/mo): +2 points
      // - Low savings (<$200/mo): +1 point
      // - Low risk (1): +2 points (risk is very important)
      // - Medium risk (2): +1 point
      // - High risk (3): +0 points

      let priorityScore = 0;

      // Savings dollar amount contribution
      if (savingsValue > 500) priorityScore += 3;
      else if (savingsValue > 200) priorityScore += 2;
      else priorityScore += 1;

      // Risk contribution (inverse - lower risk = higher priority)
      if (performanceRisk === 1)
        priorityScore += 2; // Low risk adds significant points
      else if (performanceRisk === 2) priorityScore += 1; // Medium risk adds some points

      // Determine color based on priority score
      // Score 5: Red (>$500/mo + low risk) - DO THIS NOW
      // Score 3-4: Amber (medium-high savings with medium risk, or good savings without low risk)
      // Score 1-2: Green (lower savings or high risk)
      let priorityColor;
      let priorityLabel;

      if (priorityScore >= 5) {
        priorityColor = "#f44336"; // Red - DO THIS NOW (high $ + low risk)
        priorityLabel = "High Priority";
      } else if (priorityScore >= 3) {
        priorityColor = "#ff9800"; // Amber - Should do
        priorityLabel = "Medium Priority";
      } else {
        priorityColor = "#4caf50"; // Green - Consider
        priorityLabel = "Low Priority";
      }

      return {
        id: rec.id || index,
        type: "rightsizing",
        title: `${rec.currentInstanceType} → ${bestOption?.instanceType || "Optimize"}`,
        description: overprovisionedDesc,
        impact: `$${savingsValue.toFixed(2)}`,
        savingsValue: savingsValue, // Keep raw value for sorting
        severity: priorityLabel,
        confidence: savingsPercent > 0 ? Math.round(savingsPercent) : null, // Only show if we have actual data
        icon: <Speed />,
        color: priorityColor,
        priorityScore: priorityScore, // For sorting
        recommendation: bestOption
          ? `Downsize to ${bestOption.instanceType} (${savingsPercent.toFixed(1)}% savings)`
          : "No recommendation available",
        riskLevel:
          bestOption?.performanceRisk === 1
            ? "low"
            : bestOption?.performanceRisk === 2
              ? "medium"
              : bestOption?.performanceRisk === 3
                ? "high"
                : null,
        instanceId: rec.instanceId, // Store instance ID for modal lookup
        rawData: rec, // Store full recommendation data
        resourceMetrics, // Add resource utilization data
      };
    });

    // Sort by priority score first, then by savings value (highest first) and take top 20
    const sortedOptimizations = costOptimization
      .sort((a, b) => {
        // First sort by priority score (higher is better)
        if (b.priorityScore !== a.priorityScore) {
          return b.priorityScore - a.priorityScore;
        }
        // Then by savings value
        return b.savingsValue - a.savingsValue;
      })
      .slice(0, 20);

    // Calculate total potential savings
    const totalSavings = sortedOptimizations.reduce((sum, opt) => sum + opt.savingsValue, 0);

    return {
      costOptimization:
        sortedOptimizations.length > 0
          ? sortedOptimizations
          : [
              {
                id: 1,
                type: "rightsizing",
                title: "No Optimization Data",
                description: "No compute optimizer recommendations available at this time",
                impact: "$0",
                severity: "low",
                confidence: 0,
                icon: <Speed />,
                color: "#9e9e9e",
                recommendation: "Check back later for new recommendations",
                timeToImplement: "N/A",
                riskLevel: "low",
              },
            ],
      totalSavings: totalSavings,
      anomalies: [],
      predictions: [],
    };
  };

  const insights = getInsightsData();

  const handleEc2Click = async (insight) => {
    if (insight.instanceId) {
      try {
        // Fetch full EC2 data from API using the instance ID
        const ec2Data = await callAPI(
          `assets/ec2/ec2ById?id=${insight.instanceId}`,
          "GET",
          null,
          true
        );
        setSelectedEc2(ec2Data);
        setModalOpen(true);
      } catch (error) {
        console.error("Failed to fetch EC2 details:", error);
      }
    }
  };

  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index} style={{ height: "100%", overflow: "hidden" }}>
      {value === index && (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>{children}</Box>
      )}
    </div>
  );

  const InsightCard = ({ insight, showDetails = false }) => (
    <Card
      onClick={() => handleEc2Click(insight)}
      sx={{
        mb: 2,
        background: isDark
          ? `linear-gradient(135deg, ${alpha(insight.color, 0.1)} 0%, transparent 50%)`
          : `linear-gradient(135deg, ${alpha(insight.color, 0.05)} 0%, transparent 50%)`,
        border: `1px solid ${alpha(insight.color, 0.2)}`,
        borderLeft: `4px solid ${insight.color}`,
        transition: "all 0.3s ease",
        animation: `${fadeInUp} 0.6s ease-out`,
        position: "relative",
        overflow: "hidden",
        overflowX: "hidden",
        cursor: insight.instanceId ? "pointer" : "default",
        "&:hover": {
          transform: "translateY(-4px) scale(1.02)",
          boxShadow: `0 12px 40px ${alpha(insight.color, 0.25)}`,
          borderColor: alpha(insight.color, 0.4),
          "&::before": {
            opacity: 1,
          },
        },
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "2px",
          background: `linear-gradient(90deg, transparent, ${insight.color}, transparent)`,
          opacity: 0,
          transition: "opacity 0.3s ease",
        },
      }}
    >
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 }, overflow: "hidden" }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, overflow: "hidden" }}>
          <Avatar
            sx={{
              bgcolor: alpha(insight.color, 0.1),
              color: insight.color,
              width: 40,
              height: 40,
            }}
          >
            {insight.icon}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
            {/* Top line - full width description */}
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 1.5,
                lineHeight: 1.4,
                fontWeight: 500,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {insight.description}
            </Typography>

            {/* Bottom section - savings and metrics */}
            <Box sx={{ display: "flex", gap: 2, overflow: "hidden" }}>
              {/* Left side - Cost savings */}
              <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                <Box sx={{ mb: 0.5 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: "0.7rem", mb: 0.5, display: "block" }}
                  >
                    Potential Cost Saving
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ color: insight.color, fontWeight: 700, fontSize: "1.5rem" }}
                  >
                    ${(insight.savingsValue || 0).toFixed(2)}
                    <Typography
                      component="span"
                      sx={{ fontSize: "0.9rem", fontWeight: 500, ml: 0.5 }}
                    >
                      /mo
                    </Typography>
                  </Typography>
                </Box>

                {showDetails && (
                  <>
                    {insight.recommendation && (
                      <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, mt: 1 }}>
                        💡 {insight.recommendation}
                      </Typography>
                    )}
                    {insight.riskLevel && (
                      <Typography variant="caption" color="text.secondary">
                        ⚠️ Performance risk: {insight.riskLevel}
                      </Typography>
                    )}
                  </>
                )}
              </Box>

              {/* Right side - Vertical resource bars */}
              {showDetails &&
                insight.resourceMetrics &&
                Object.keys(insight.resourceMetrics).length > 0 && (
                  <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-end", minWidth: 200 }}>
                    {Object.entries(insight.resourceMetrics).map(([key, metric]) => (
                      <Box
                        key={key}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <Box
                          sx={{
                            width: 32,
                            height: 80,
                            bgcolor: alpha(metric.color, 0.2),
                            borderRadius: 1,
                            overflow: "hidden",
                            display: "flex",
                            flexDirection: "column-reverse",
                          }}
                        >
                          <Box
                            sx={{
                              height: `${Math.min(metric.barWidth, 100)}%`,
                              width: "100%",
                              bgcolor: metric.color,
                              transition: "height 0.3s ease",
                            }}
                          />
                        </Box>
                        <Typography
                          variant="caption"
                          color={metric.color}
                          sx={{ fontSize: "0.7rem", fontWeight: 600 }}
                        >
                          {metric.label}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: "0.65rem" }}
                        >
                          {metric.name}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const tabs = [
    { label: "Top EC2 Optimizations", icon: <Savings />, data: insights.costOptimization },
    { label: "Anomalies", icon: <Warning />, data: insights.anomalies },
    { label: "AI Analysis", icon: <AutoAwesome />, data: insights.predictions },
  ];

  const handleDownload = () => {
    // Prepare data for Excel export based on active tab
    const currentTab = tabs[activeTab];
    let exportData = [];

    if (activeTab === 0) {
      // EC2 Optimizations
      exportData = insights.costOptimization.map((opt) => ({
        "Instance ID": opt.instanceId || "N/A",
        "Current Type": opt.title.split(" → ")[0],
        "Recommended Type": opt.title.split(" → ")[1],
        "Monthly Savings": opt.impact,
        "Savings Percentage": opt.confidence ? `${opt.confidence}%` : "N/A",
        Priority: opt.severity,
        "Risk Level": opt.riskLevel || "N/A",
        Recommendation: opt.recommendation,
        Description: opt.description,
      }));
    } else if (activeTab === 1) {
      // Anomalies
      exportData = insights.anomalies.map((anomaly) => ({
        Title: anomaly.title,
        Description: anomaly.description,
        Severity: anomaly.severity,
        Impact: anomaly.impact,
        Confidence: `${anomaly.confidence}%`,
        Timestamp: anomaly.timestamp,
        Details: anomaly.details,
      }));
    } else if (activeTab === 2) {
      // Predictions
      exportData = insights.predictions.map((pred) => ({
        Title: pred.title,
        Description: pred.description,
        Probability: `${pred.probability}%`,
        Timeframe: pred.timeframe,
        Impact: pred.impact,
        Recommendation: pred.recommendation,
      }));
    }

    DownloadExcel(`AI_Finance_Insights_${currentTab.label}`, exportData);
  };

  return (
    <WidgetContainer
      title="AI Finance Insights"
      updatedAt={`${new Date().toLocaleTimeString()}`}
      loaded={true}
      draggable={false}
      editMode={false}
      insights={false}
      maximize={true}
      download={true}
      downloadTooltip="Download AI Finance Insights (Excel)"
      onDownload={handleDownload}
    >
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          background: isDark
            ? alpha(theme.palette.background.paper, 0.4)
            : alpha(theme.palette.background.paper, 0.6),
          borderRadius: "12px",
          overflow: "hidden",
          position: "relative",
          overflowX: "hidden",
        }}
      >
        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2, pt: 1 }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="fullWidth"
            sx={{
              "& .MuiTab-root": {
                minHeight: 48,
                textTransform: "none",
                fontWeight: 500,
                fontSize: "0.85rem",
              },
            }}
          >
            {tabs.map((tab, index) => (
              <Tab
                key={index}
                icon={tab.icon}
                label={tab.label}
                iconPosition="start"
                sx={{
                  gap: 1,
                  "& .MuiSvgIcon-root": { fontSize: "1.1rem" },
                }}
              />
            ))}
          </Tabs>
        </Box>

        {/* Tab Content */}
        <Box sx={{ flex: 1, overflow: "hidden", p: 2, display: "flex", flexDirection: "column" }}>
          {tabs.map((tab, index) => (
            <TabPanel key={index} value={activeTab} index={index}>
              <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
                {/* Cost Optimization Summary Banner - Fixed at top */}
                {index === 0 && insights.totalSavings > 0 && (
                  <Box
                    sx={{
                      mb: 2,
                      p: 1.5,
                      background: isDark
                        ? alpha(theme.palette.success.dark, 0.15)
                        : alpha(theme.palette.success.light, 0.15),
                      borderLeft: `4px solid ${theme.palette.success.main}`,
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexShrink: 0,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <MonetizationOn
                        sx={{ fontSize: "1.5rem", color: theme.palette.success.main }}
                      />
                      <Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: "0.75rem" }}
                        >
                          Total Potential Savings
                        </Typography>
                        <Typography variant="h6" fontWeight={700} color="success.main">
                          ${insights.totalSavings.toFixed(2)}/mo
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {tab.data.length} opportunities
                    </Typography>
                  </Box>
                )}

                {/* Scrollable content area */}
                <Box
                  sx={{
                    flex: 1,
                    overflowY: "auto",
                    "&::-webkit-scrollbar": { width: 6 },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: alpha(theme.palette.divider, 0.1),
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: alpha(theme.palette.primary.main, 0.3),
                      borderRadius: 3,
                    },
                  }}
                >
                  {tab.data.length > 0 ? (
                    tab.data.map((insight) => (
                      <InsightCard
                        key={insight.id}
                        insight={insight}
                        showDetails={index === 0} // Show details for optimization tab
                      />
                    ))
                  ) : index === 2 ? (
                    /* Predictions tab - AI Cost Analysis */
                    <Box sx={{ px: 1 }}>
                      {/* Disclaimer */}
                      <Box
                        sx={{
                          padding: "10px 12px",
                          marginBottom: "16px",
                          backgroundColor: isDark
                            ? "rgba(255, 255, 255, 0.05)"
                            : "rgba(0, 0, 0, 0.03)",
                          border: `1px solid ${isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"}`,
                          borderRadius: "6px",
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1.5,
                        }}
                      >
                        <InfoOutlined sx={{ fontSize: "1.1rem", color: "text.secondary", mt: "2px", flexShrink: 0 }} />
                        <Typography variant="body2" sx={{ color: "text.primary", opacity: 0.85, flex: 1, fontSize: "0.8rem" }}>
                          This summary is generated with the assistance of AI. While care is taken to ensure
                          accuracy, the output may contain errors or omissions and should be reviewed before
                          being used for decision-making.
                        </Typography>
                      </Box>

                      {/* Loading State */}
                      {aiAnalysisLoading && (
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 3 }}>
                          <CircularProgress size={40} sx={{ color: "#667eea", marginBottom: 1.5 }} />
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.875rem" }}>
                            Analyzing your AWS costs...
                          </Typography>
                        </Box>
                      )}

                      {/* Content */}
                      {!aiAnalysisLoading && aiAnalysisData && (
                        <Box>
                          {/* Executive Summary */}
                          {aiAnalysisData.summary && (
                            <Box sx={{ marginBottom: 2.5 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: "bold", marginBottom: 1, fontSize: "0.95rem", color: "text.primary" }}>
                                Executive Summary
                              </Typography>
                              <Paper
                                sx={{
                                  padding: "12px",
                                  borderRadius: "6px",
                                  backgroundColor: isDark ? undefined : "#f0f0f0",
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  component="div"
                                  sx={{ whiteSpace: "pre-wrap", lineHeight: 1.75, fontWeight: 300, fontSize: "0.85rem", "& strong": { fontWeight: "bold" } }}
                                >
                                  {renderTextWithBold(aiAnalysisData.summary)}
                                </Typography>
                              </Paper>
                            </Box>
                          )}

                          {/* Dynamic Sections */}
                          {aiAnalysisData.sections && aiAnalysisData.sections.length > 0 && aiAnalysisData.sections.map((section, sIdx) => (
                            <Box key={sIdx} sx={{ marginBottom: 2.5 }}>
                              <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: "bold", marginBottom: 1, fontSize: "0.95rem", color: "text.primary" }}
                              >
                                {section.title}
                              </Typography>
                              <Paper
                                sx={{
                                  padding: "12px",
                                  borderRadius: "6px",
                                  backgroundColor: isDark ? undefined : "#f0f0f0",
                                }}
                              >
                                {section.summary && (
                                  <Typography
                                    variant="body2"
                                    component="div"
                                    sx={{ whiteSpace: "pre-wrap", lineHeight: 1.5, marginBottom: 1.5, fontWeight: 300, fontSize: "0.85rem", "& strong": { fontWeight: "bold" } }}
                                  >
                                    {renderTextWithBold(section.summary)}
                                  </Typography>
                                )}
                                {section.bulletPoints && section.bulletPoints.length > 0 && (
                                  <Box component="ul" role="list" sx={{ margin: 0, paddingLeft: 0.5, listStyle: "none" }}>
                                    {section.bulletPoints.map((bullet, bulletIndex) => (
                                      <Box component="li" role="listitem" key={bulletIndex} sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 0.5 }}>
                                        <Box component="span" aria-hidden="true" sx={{ fontSize: "0.85rem", color: "#2196F3", mt: "0px", flexShrink: 0 }}>✦</Box>
                                        <Typography
                                          variant="body2"
                                          component="span"
                                          sx={{ lineHeight: 1.5, fontWeight: 300, fontSize: "0.85rem", "& strong": { fontWeight: "bold" } }}
                                        >
                                          {renderTextWithBold(bullet)}
                                        </Typography>
                                      </Box>
                                    ))}
                                  </Box>
                                )}
                              </Paper>
                            </Box>
                          ))}
                        </Box>
                      )}

                      {/* Error State */}
                      {!aiAnalysisLoading && !aiAnalysisData && (
                        <Box sx={{ textAlign: "center", py: 3 }}>
                          <Typography variant="body2" color="error" sx={{ fontSize: "0.875rem" }}>
                            Failed to load AI analysis. Please try again.
                          </Typography>
                          <Button
                            variant="contained"
                            onClick={() => { setPredictionsFetched(false); }}
                            sx={{ marginTop: 1.5, fontSize: "0.875rem", padding: "6px 16px" }}
                          >
                            Retry
                          </Button>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                        gap: 2,
                        py: 4,
                      }}
                    >
                      <AutoAwesome
                        sx={{
                          fontSize: "4rem",
                          color: alpha(theme.palette.primary.main, 0.3),
                          animation: `${pulse} 2s infinite`,
                        }}
                      />
                      <Typography
                        variant="h6"
                        fontWeight={600}
                        color="text.secondary"
                        sx={{ opacity: 0.7 }}
                      >
                        Coming Soon
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ opacity: 0.5, maxWidth: 300, textAlign: "center" }}
                      >
                        AI-powered {tab.label.toLowerCase()} analysis will be available soon
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </TabPanel>
          ))}
        </Box>

        {/* Footer with AI branding */}
        <Box
          sx={{
            p: 1.5,
            borderTop: 1,
            borderColor: "divider",
            background: alpha(theme.palette.primary.main, 0.05),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <Psychology sx={{ fontSize: "1rem", color: "primary.main" }} />
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
            Powered by CoraAI
          </Typography>
        </Box>
      </Box>

      {/* EC2 Details Modal */}
      <Ec2DetailsModal open={modalOpen} onClose={() => setModalOpen(false)} data={selectedEc2} />
    </WidgetContainer>
  );
};

export default AIFinanceInsights;
